import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ProtectedPageSidebar from '../../components/Sidebar/ProtectedPageSidebar';
import StoryboardEditor from '../../components/StoryboardEditor/StoryboardEditor';
import { ORIENTATIONS, PROJECT_STATE } from '../../constants/projectCreatorConsts';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { ToastNotification } from '../../components/Toast/Toast';
import { useUserContext } from '../../context/UserContext';
import { useHistory, useLocation } from 'react-router-dom';
import VideoService from '../../service/VideoService';
import CmsAPI from '../../../common/apis/CmsAPI';
import { withPermissionCheck } from '../../hocs/withPermissionCheck';
import queryString from 'query-string';
import { useSceneJobs } from 'engage/features/Storyboard/hooks/useSceneJobs';
import { ScenesType } from '../../../common/apis/CmsAPI';
import { StoryboardProvider } from '../../context/StoryboardContext';
import useWaitForJobsToStart from 'engage/hooks/useWaitForJobsToStart';
import { useStoryScenes } from 'engage/features/Storyboard/hooks/useStoryScenes';
import { useStoryPermissions } from 'engage/features/Storyboard/hooks/useStoryPermissions';

const Storyboard = ({ videoId, initialVideoDetails, userPermissions, storyRole }) => {
  const [projectState, setProjectState] = useState(PROJECT_STATE.FILL_THE_FORM);
  const [projectName, setProjectName] = useState('Project name');
  const [init, setInit] = useState(false);
  const { user, appConfig } = useUserContext();
  const [video, setVideo] = useState(null);
  const [skipGeneration, setSkipGeneration] = useState(false);
  const [generationRequest, setGenerationRequest] = useState(null);
  const [selectedOrientation, setSelectedOrientation] = useState(ORIENTATIONS.LANDSCAPE);

  const { assignments,
    permissions,
    manageTeam,
    inviteForScenes,
    remindUser,
    reviseRequest,
    changeUserPermissions,
    completeUserAssignments,
  } = useStoryPermissions({ videoId, videoAuthor: video?.author });

  const {
    loading,
    permittedScenes,
    currentScene,
    initJobs,
    initScenes,
    addScene,
    duplicateScene,
    copyScenesIntoStory,
    deleteScene,
    moveScene,
    changeScenesOrientation,
    changeScene,
    selectScene,
    requestSceneUpdate,
    initJobsAdded,
  } = useStoryScenes({
    videoId,
    video,
    assignments,
    permissions,
    userPermissions
  })

  useEffect(() => {
    if (initJobs.length) {
      pushJobs(initJobs);
      initJobsAdded();
    }
  }, [initJobs])

  function onJobsFound(jobsList) {
    pushJobs(jobsList);
  }

  function onScenesReady(scenesList) {
    scenesList.forEach(scene => {
      changeScene(scene.id, {
        processing: false
      });
    });
  }

  const { pushScenes } = useWaitForJobsToStart({ onJobsFound, onScenesReady });

  const location = useLocation();
  const history = useHistory();

  const folder = useMemo(() => (video ? video?.folder : location.state?.folder ?? null), [location, video]);

  const createRightAway = useMemo(() => videoId === 'new' && location.state?.create, [location]);

  const videoIdFromLocation = useMemo(() => queryString.parse(location.search)?.videoId, [location]);

  const { t } = useTranslation();

  function onJobProcessing(_, jobDetails) {
    !jobDetails.outputs.find(item => item.label === 'hlsTrimmed') && changeScene(jobDetails?.outputs[0]?.relatedTo?.id, {
      processing: true,
    });
  }

  async function onJobSucceed(_, jobDetails) {
    if (jobDetails.outputs.find(item => item.label == "enhanced" || item.label == 'avatar')) {
      const response = await VideoService.checkSceneJobs(videoId, jobDetails?.outputs[0]?.relatedTo?.id);
      let checkDetails = true;
      if (response.hasRunningJobs) {
        const sceneJob = response.runningJobs.find(
          jobData => jobData.outputs.find(output => output.relatedTo.id === jobDetails?.outputs[0]?.relatedTo?.id && output.type == 'hls') !== undefined
        );
        if (sceneJob.outputs.filter(output => output.label !== 'hlsTrimmed').length > 0) {
          pushJobs([sceneJob.jobId]);
          return;
        }
      }
      requestSceneUpdate(jobDetails?.outputs[0]?.relatedTo?.id, checkDetails);
    } else {
      requestSceneUpdate(jobDetails?.outputs[0]?.relatedTo?.id);
    }
  };

  function onJobFailed(jobDetails) {
    console.log('Job failed', jobDetails);
    changeScene(jobDetails?.outputs[0]?.relatedTo?.id, {
      processing: false,
    });
  }

  const { pushJobs } = useSceneJobs({
    onJobSucceed,
    onJobProcessing,
    onJobFailed
  });


  const changeProjectOrientation = orientation => {
    setSelectedOrientation(orientation);
  };

  const requestIsNarrativesGenerated = (tries = 0) => {
    let status = '';
    // TODO: add backend request to INSERTAPICALL
    const response = {};
    status = response?.status;

    if ((status === 'pending' || status === 'processing') && tries <= 10) {
      setTimeout(async () => {
        requestIsNarrativesGenerated(tries + 1);
      }, 5000);
      return;
    }

    if (status == 'pending_scripts') {
      setProjectState(PROJECT_STATE.GENERATING_SCRIPT);
      setTimeout(async () => {
        requestIsNarrativesGenerated(tries + 1);
      }, 5000);
      return;
    }

    if (status == 'succeeded') {
      setProjectState(PROJECT_STATE.NARRATIVES_EDITING);
    }
  };

  const generateNarratives = async payload => {
    setProjectState(PROJECT_STATE.GENERATING_NARRATIVES);
    try {
      const response = await VideoService.createStoryboard({ ...payload, name: projectName });
      if (folder) {
        await VideoService.updateVideo(response.id, { folder });
      }
      if (response?.id) {
        history.replace(`/eg/storyboard/${response.id}`);
      }
      setProjectState(PROJECT_STATE.NARRATIVES_EDITING);
    } catch (e) {
      console.log('Error while generating', e);
      ToastNotification({ message: t('storyboard.steps.generating_error') });
    }
  };

  const skipGenerateNarratives = async () => {
    setSkipGeneration(true);
    setProjectState(PROJECT_STATE.GENERATING_SCRIPT);
    try {
      const id = uuidv4();
      const name = projectName;
      const orientation = selectedOrientation.type;
      const source = 'web';
      const scenes = [
        {
          id: uuidv4(),
          name: `${t('storyboard.editor.default_name')} 1`,
          orientation: selectedOrientation.type,
        },
      ];

      const response = await CmsAPI.insertStory({
        id,
        name,
        orientation,
        source,
        scenes,
      });
      await VideoService.updateVideo(id, { folder });
      if (response?.result?.id) {
        history.replace(`/eg/storyboard/${response.result.id}`);
      }
      setProjectState(PROJECT_STATE.NARRATIVES_EDITING);
    } catch (e) {
      console.log('Error while generating', e);
      ToastNotification({ message: t('storyboard.steps.generating_error') });
    }
  };

  const updateProjectName = useCallback(
    async name => {
      if (name.trim().length && name !== projectName) {
        if (videoId !== 'new') {
          try {
            await VideoService.updateVideo(videoId, { name: name });
            ToastNotification({ message: t('clip_organizer.toast.successfully_saved'), iconName: 'success' });
          } catch (e) {
            console.log('Error while updating story name', e);
          }
        }
        setProjectName(name);
      }
    },
    [videoId, projectName, setProjectName]
  );

  const copyProjectLink = () => {
    navigator.clipboard.writeText(VideoService.getProjectUrl(videoId));
    ToastNotification({
      message: `${t('storyboard.editor.project_link_copied')}`,
      iconName: 'success',
    });
  };

  const checkSceneJobs = useCallback(async (sceneId, jobId = null, fallback = null) => {
    if (!sceneId) {
      return;
    }
    if (jobId !== null) {
      pushJobs([jobId]);
      return;
    }
    const storyJobs = await VideoService.checkSceneJobs(videoId, sceneId);

    const sceneJob = storyJobs.runningJobs.find(
      jobData => jobData.outputs.find(output => output.relatedTo.id === sceneId && output.type == 'hls') !== undefined
    );
    if (sceneJob) {
      pushJobs([sceneJob.jobId]);
      changeScene(sceneId, {
        processing: true,
        recording: false,
        hidden: false,
      });
    } else {
      if (fallback) {
        fallback();
        return;
      }
      pushScenes([{ videoId, sceneId }]);
    }
  }, [])


  const applyAssistantChanges = useCallback(
    async preparedData => {
      try {
        // Save story changes - remove/add scenes, update title
        const scenesToUpdate = preparedData.scenes.map((item, index) => {
          const { name, script, narrative } = item;
          if (permittedScenes[index]) {
            return { id: permittedScenes[index].id, name, script, narrative };
          } else {
            const sceneId = uuidv4();
            return { id: sceneId, newSceneId: sceneId, name, script, narrative };
          }
        });
        await VideoService.updateVideo(videoId, { name: preparedData.name, scenes: scenesToUpdate });
        await CmsAPI.bulkUpdateScenes(
          scenesToUpdate.map(({ id, script, narrative }) => ({ id, script, narrative, storyId: videoId }))
        );
        const videoDetails = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
        initScenes(videoDetails.scenes, false);
        setProjectName(videoDetails.name);
        ToastNotification({ message: t('clip_organizer.toast.successfully_saved'), iconName: 'success' });
      } catch (e) {
        console.log('Error e', e);
        ToastNotification({
          message: t('clip_organizer.toast.error_while_saving'),
        });
      }
    },
    [permittedScenes, videoId, t]
  );

  // TODO: remove after backend ready. ONLY FOR UI TEST
  useEffect(() => {
    if (projectState === PROJECT_STATE.GENERATING_NARRATIVES) {
      setTimeout(() => {
        setProjectState(PROJECT_STATE.GENERATING_SCRIPT);
      }, 3000);
    }
  }, [projectState]);

  useEffect(() => {
    const loadStoryDetails = async () => {
      try {
        const { readStory, updateStory, allowedScenes } = userPermissions;

        if (!readStory && !updateStory && !allowedScenes.length) {
          history.push('/eg/dashboard');
        }

        try {
          const result = await VideoService.getStoryStoryboardRequest(videoIdFromLocation ?? videoId);
          result.length && setGenerationRequest(result.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0]);
        } catch (e) {
          console.log('Error', e);
        }

        if (videoIdFromLocation) {
          setInit(true);
          return;
        }

        let videoDetails = initialVideoDetails ? structuredClone(initialVideoDetails) : null;
        if (!initialVideoDetails?.scenes || videoId !== 'new') {
          videoDetails = await VideoService.getVideoDetails(videoId, ScenesType.REGULAR);
        }
        initScenes(videoDetails.scenes, true);
        setVideo(videoDetails);
        setProjectName(videoDetails.name);
        setProjectState(PROJECT_STATE.NARRATIVES_EDITING);

        setInit(true);
      } catch (e) {
        console.log('Error', e);
      }
    };
    if (!init) {
      if (videoId !== 'new' || videoIdFromLocation) {
        loadStoryDetails();
      } else {
        setInit(true);
      }
    }
  }, [user, appConfig]);

  useEffect(() => {
    if (createRightAway) {
      skipGenerateNarratives();
    }
  }, [createRightAway]);

  const contextValue = {
    loading,
    videoId: videoId,
    video: video,
    storyRole: storyRole,
    scenes: permittedScenes,
    currentScene,
    assignments,
    storyPermissions: permissions,
    userPermissions,
    projectName,
    generationRequest,
    addScene,
    duplicateScene,
    copyScenesIntoStory,
    deleteScene,
    inviteForScenes,
    checkSceneJobs,
    updateProjectName,
    changeScene,
    moveScene,
    selectScene,
    manageTeam,
    remindUser,
    copyProjectLink,
    reviseRequest,
    changeUserPermissions,
    changeScenesOrientation,
    completeUserAssignments,
  }

  return (
    <>
      <ProtectedPageSidebar />
      <StoryboardProvider value={contextValue}>
        <StoryboardEditor
          init={init}
          video={video}
          projectState={projectState}
          skipGeneration={skipGeneration}
          generateNarratives={generateNarratives}
          skipGenerateNarratives={skipGenerateNarratives}
          selectedOrientation={selectedOrientation}
          changeProjectOrientation={changeProjectOrientation}
          applyAssistantChanges={applyAssistantChanges}
          pushJobs={pushJobs}
        />
      </StoryboardProvider>
    </>
  );
};
export default withPermissionCheck(Storyboard);
